import { classifyEnvironmentId } from "./environments/environmentIdClassification";

/** A group member, as deletion needs to see it. */
interface DeletableMember {
  readonly environmentId: string;
  readonly id: string;
  readonly workspaceRoot?: string;
  readonly connectionLabel?: string;
}

/** One checkout, described for the confirm dialog. */
interface DeletionCheckout {
  readonly cwd: string;
  readonly label: string;
  readonly environmentRole: "local" | "cloud";
}

export interface ProjectGroupDeletionPlan {
  /** Handed straight to `createRemovableForgeProject`. */
  readonly targets: readonly { readonly environmentId: string; readonly projectId: string }[];
  /** Shaped for `SidebarRemoveProjectDialog`'s summary. */
  readonly summary: {
    readonly checkoutCount: number;
    readonly threadCount: number;
    readonly checkouts: readonly DeletionCheckout[];
  };
  /** True when any checkout is a cloud runtime, whose removal destroys a volume. */
  readonly removesCloudRuntime: boolean;
  /** False for an empty group, which has nothing to delete. */
  readonly deletable: boolean;
}

/**
 * What deleting this project would actually reach.
 *
 * A group is one repository seen from several machines, so "delete this
 * project" means every checkout of it. Computed rather than assumed because the
 * confirm dialog reads this, and its sentence is the last thing between someone
 * and a destroyed sprite volume — "delete forge" reads very differently when it
 * means one checkout versus twenty, and twenty is a real number for this repo.
 *
 * The removal itself is NOT reimplemented. `removeSidebarProject` already owns
 * runtime teardown, per-step progress and the CMMD archive-then-delete cascade;
 * a second path that skipped it would strand links and runtimes. This decides
 * only WHAT to hand it, and `createRemovableForgeProject` turns the targets into
 * one removal covering every member.
 */
export function planProjectGroupDeletion(
  members: readonly DeletableMember[],
  threadCount: number,
): ProjectGroupDeletionPlan {
  const checkouts = members.map((member): DeletionCheckout => {
    // A cloud checkout, sprite or durable Environment alike, IS its working
    // copy, so removing it destroys a runtime and its volume. A local checkout
    // keeps its files. The dialog has to be able to say which.
    const isCloud = classifyEnvironmentId(member.environmentId).isCloud;
    return {
      cwd: member.workspaceRoot ?? "",
      label: member.connectionLabel ?? member.environmentId,
      environmentRole: isCloud ? "cloud" : "local",
    };
  });

  return {
    targets: members.map((member) => ({
      environmentId: member.environmentId,
      projectId: member.id,
    })),
    summary: {
      checkoutCount: members.length,
      threadCount,
      checkouts,
    },
    removesCloudRuntime: checkouts.some((checkout) => checkout.environmentRole === "cloud"),
    // `createRemovableForgeProject` throws on an empty target list, so the row
    // hides itself rather than offering a button that would throw.
    deletable: members.length > 0,
  };
}
